import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { alpha, useTheme } from '@mui/material/styles';
import AdminDashboard from 'layouts/AdminDashboard';
import Container from 'components/Container';
import { getUserById, updateUser } from './UserService';

interface AdminUserDetail {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  mobileNumber: string | null;
  enabled: boolean;
  roles: string[];
  createdAt: string;
  updatedAt: string;
}

const ASSIGNABLE_ROLES = ['ROLE_USER', 'ROLE_MANAGER', 'ROLE_ADMIN'];

const ROLE_LABELS: Record<string, string> = {
  ROLE_USER: 'User',
  ROLE_MANAGER: 'Manager',
  ROLE_ADMIN: 'Admin',
};

const initials = (u: AdminUserDetail) =>
  ((u.firstName?.[0] ?? '') + (u.lastName?.[0] ?? '')).toUpperCase() || '?';

const fmtDate = (ts: string) =>
  ts
    ? new Date(ts).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
    : '—';

const primaryRole = (roles: string[]): string => {
  if (roles.includes('ROLE_ADMIN')) return 'ROLE_ADMIN';
  if (roles.includes('ROLE_MANAGER')) return 'ROLE_MANAGER';
  if (roles.includes('ROLE_USER')) return 'ROLE_USER';
  return 'ROLE_USER';
};

const UserDetailPage = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [user, setUser] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Edit form state
  const [enabled, setEnabled] = useState(false);
  const [roleName, setRoleName] = useState('ROLE_USER');
  const [reason, setReason] = useState('');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (!id) return;
    getUserById(Number(id))
      .then((res) => {
        const u: AdminUserDetail = res.data;
        setUser(u);
        setEnabled(u.enabled);
        setRoleName(primaryRole(u.roles));
      })
      .catch(() => setLoadError('Could not load user.'))
      .finally(() => setLoading(false));
  }, [id]);

  const isDirty =
    user !== null &&
    (enabled !== user.enabled || roleName !== primaryRole(user.roles));

  const handleSave = () => {
    if (!id || !isDirty) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError('');

    const body: { enabled?: boolean; roleName?: string; reason?: string } = {};
    if (enabled !== user!.enabled) body.enabled = enabled;
    if (roleName !== primaryRole(user!.roles)) body.roleName = roleName;
    if (reason.trim()) body.reason = reason.trim();

    updateUser(Number(id), body)
      .then((res) => {
        const u: AdminUserDetail = res.data;
        setUser(u);
        setEnabled(u.enabled);
        setRoleName(primaryRole(u.roles));
        setReason('');
        setSaveSuccess(true);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ??
          err?.response?.data ??
          'Failed to save changes.';
        setSaveError(String(msg));
      })
      .finally(() => setSaving(false));
  };

  return (
    <AdminDashboard>
      <Box
        sx={{
          background: `linear-gradient(to bottom, ${alpha(
            theme.palette.primary.main,
            0.06,
          )}, transparent)`,
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Container>
          {/* Back button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/users')}
            sx={{ mb: 3 }}
          >
            Users
          </Button>

          {loadError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loadError}
            </Alert>
          )}

          {loading ? (
            <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
              <CardContent sx={{ p: 3 }}>
                <Skeleton variant="circular" width={64} height={64} sx={{ mb: 2 }} />
                <Skeleton width={220} height={32} sx={{ mb: 1 }} />
                <Skeleton width={180} />
              </CardContent>
            </Card>
          ) : user ? (
            <>
              {/* Profile card */}
              <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2], mb: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 64,
                        height: 64,
                        fontSize: '1.4rem',
                        bgcolor: alpha(theme.palette.primary.main, 0.85),
                      }}
                    >
                      {initials(user)}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" fontWeight={700}>
                        {user.firstName} {user.lastName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Mobile
                      </Typography>
                      <Typography variant="body2">
                        {user.mobileNumber ?? '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Roles
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
                        {user.roles.map((r) => (
                          <Chip
                            key={r}
                            label={r.replace('ROLE_', '')}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Joined
                      </Typography>
                      <Typography variant="body2">{fmtDate(user.createdAt)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Last updated
                      </Typography>
                      <Typography variant="body2">{fmtDate(user.updatedAt)}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Edit card */}
              <Card sx={{ borderRadius: 3, boxShadow: theme.shadows[2] }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
                    Account settings
                  </Typography>

                  {saveSuccess && (
                    <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSaveSuccess(false)}>
                      Changes saved successfully.
                    </Alert>
                  )}
                  {saveError && (
                    <Alert severity="error" sx={{ mb: 3 }} onClose={() => setSaveError('')}>
                      {saveError}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={enabled}
                          onChange={(e) => setEnabled(e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Account enabled
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Disabled accounts cannot log in
                          </Typography>
                        </Box>
                      }
                    />

                    <FormControl size="small" sx={{ maxWidth: 280 }}>
                      <InputLabel>Role</InputLabel>
                      <Select
                        value={roleName}
                        label="Role"
                        onChange={(e) => setRoleName(e.target.value)}
                      >
                        {ASSIGNABLE_ROLES.map((r) => (
                          <MenuItem key={r} value={r}>
                            {ROLE_LABELS[r]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      label="Reason (optional)"
                      placeholder="e.g. Promoted to admin following review"
                      size="small"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      sx={{ maxWidth: 480 }}
                      helperText="Recorded in the audit log"
                    />

                    <Box>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!isDirty || saving}
                        startIcon={saving ? <CircularProgress size={16} /> : undefined}
                      >
                        {saving ? 'Saving…' : 'Save changes'}
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </>
          ) : null}
        </Container>
      </Box>
    </AdminDashboard>
  );
};

export default UserDetailPage;
